import { FC, FormEventHandler, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useAbortSignal } from '@zoopla/shared/async-hooks';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { SearchMedium } from '@zoopla-eng/zds-components/icons';
import {
    LocationSearchInput,
    Option,
    QueryType
} from '@zoopla/listing-search-location-search-input';
import { type HousePricesPage } from '../../types';
import { trackHousePricesSearch } from '../../helpers';
import {
    formStyle,
    buttonWrapperStyle,
    inputWrapperStyle
} from './HousePricesAreaSearch.styles.css';

const buildSearchUrl = (option?: Option | null, locationText = '') => {
    const value = (option?.text || locationText).trim();

    if (!value) return null;

    const identifier = option?.id ? `${option.id}/` : '';
    const q = `?q=${encodeURIComponent(value)}&search_source=house-prices`;

    return `/house-prices/${identifier}${q}`;
};

const getOptionFromRoute = (query: ParsedUrlQuery): Option | null => {
    const s = query['search-path'];
    const searchPath = (Array.isArray(s) ? s.join('/') : s) || '';

    const text = typeof query['q'] === 'string' ? query['q'] : '';

    if (!text || !searchPath) return null;

    return {
        id: searchPath,
        text
    };
};

type HousePricesAreaSearchProps = {
    buttonLabel?: string;
    parent: HousePricesPage;
    // Uses the router query values to work out what value should prepopulate
    // the input
    initialiseSelectedItem?: boolean;
    layout: 'stacked' | 'unstacked';
};

const HousePricesAreaSearch: FC<HousePricesAreaSearchProps> = ({
    buttonLabel = 'Search',
    layout,
    parent,
    initialiseSelectedItem = false
}) => {
    const abortSignal = useAbortSignal();

    const router = useRouter();

    const initialSelectedItem = initialiseSelectedItem
        ? getOptionFromRoute(router.query)
        : null;

    // The LocationSearchInput component controls both of these values which
    // are somewhat independant, but used in conjunction with each other.
    // Even though searchSelectedItem handles the prepopulation of the combobox
    // we also need to set the input value to match otherwise our unsetter hack
    // will immediately unset it.
    const [searchInputValue, setSearchInputValue] = useState(
        initialSelectedItem?.text || ''
    );
    const [searchSelectedItem, setSearchSelectedItem] =
        useState(initialSelectedItem);

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleInputValueChange = (newValue = '') => {
        // Reset any errors when the input value changes
        if (error && newValue) setError('');

        setSearchInputValue(newValue);
    };

    const handleSelectionChange = async (option: Option | null) => {
        setSearchSelectedItem(option);

        // Upon selecting a geolocation pre-fetch the SRP assuming the user is
        // likely to submit the query thus improving perceived speed
        const prefetchUrl = buildSearchUrl(option, searchInputValue);
        if (prefetchUrl) {
            router.prefetch(prefetchUrl);
        }
    };

    // Unset the selected item when the user types to prevent it from
    // storing this value and sending them to it if they make a change, type
    // something else and then submit WITHOUT selecting an option from the
    // dropdown. We can't do this within handleInputValueChange as that is also
    // called any time handleSelectionChange is called, so it would be reset
    // whenever it was set…
    useEffect(() => {
        if (
            searchSelectedItem &&
            searchSelectedItem?.text !== searchInputValue
        ) {
            setSearchSelectedItem(null);
        }
    }, [searchSelectedItem, searchInputValue]);

    const searchSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        setLoading(true);

        const newUrl = buildSearchUrl(searchSelectedItem, searchInputValue);

        if (!newUrl) {
            setError('Please enter a valid search area');
            setLoading(false);
            return;
        }

        trackHousePricesSearch(parent, 'area');

        router.push(newUrl).then(() => {
            if (!abortSignal.current?.aborted) {
                setLoading(false);
            }
        });
    };

    return (
        <form onSubmit={searchSubmit} className={formStyle({ layout })}>
            <div className={inputWrapperStyle}>
                <LocationSearchInput
                    label="Search area"
                    placeholder="e.g. Oxford, Old Kent Road or NW3"
                    queryType={QueryType.AddressPostcodes}
                    onSelectedItemChange={handleSelectionChange}
                    onInputValueChange={handleInputValueChange}
                    errorText={error}
                    initialSelectedItem={initialSelectedItem}
                />
            </div>
            <div className={buttonWrapperStyle}>
                <ButtonLeft
                    category="primary"
                    Icon={SearchMedium}
                    loading={loading}
                    loadingText="Searching"
                >
                    {buttonLabel}
                </ButtonLeft>
            </div>
        </form>
    );
};

export default HousePricesAreaSearch;
